import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Loader2, PauseCircle, PlayCircle, ShieldCheck } from "lucide-react";
import adminApi from "../lib/adminApi";
import { ShopAdminGate } from "../components/ShopAdminGate";
import { BuildVersion } from "../components/BuildVersion";
import { UserSpendTable } from "../components/UserSpendTable";
import { TodayBudgetCard } from "../components/TodayBudgetCard";
import { AiLimitBanner } from "../components/AiLimitBanner";
import { AiLimitProbe } from "../components/AiLimitProbe";
import { AlertEmailStatus } from "../components/AlertEmailStatus";
import { Button } from "../components/ui/button";
import { adminBase, adminHomeLabel } from "../lib/adminBase";
import { usd as money } from "../lib/money";

function Stat({ label, value, testid, tone }) {
  return (
    <div data-testid={testid} className="rounded-2xl border border-border bg-card px-4 py-3">
      <p className="text-xs uppercase tracking-widest text-muted-foreground font-mono2">{label}</p>
      <p className={`font-heading text-xl font-extrabold mt-1 ${tone || ""}`}>{value}</p>
    </div>
  );
}

function SpendGateInner() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await adminApi.get("/spend-gate");
      setData(data);
    } catch {
      setError("Could not load the spend gate. Please retry.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (what, body) => {
    setBusy(what);
    setError("");
    try {
      const { data } = await adminApi.post(`/spend-gate/${what}`, body || {});
      setData((prev) => ({ ...data, viewer: data.viewer || prev?.viewer }));
      if (what === "unpause") {
        setAmount("");
        setNote("");
      }
    } catch {
      setError(`Could not ${what} the gate. Please retry.`);
    } finally {
      setBusy("");
    }
  };

  const gate = data?.gate;
  const paused = gate?.paused !== false;
  const alert = gate?.alert && !gate.alert.acked ? gate.alert : null;
  const setToday = async (budget_usd) => {
    const { data } = await adminApi.post("/spend-gate/today-budget", { budget_usd });
    setData((prev) => ({ ...data, viewer: data.viewer || prev?.viewer }));
  };
  const retryProbe = async () => {
    const { data } = await adminApi.post("/spend-gate/ai-limit/retry");
    setData((prev) => ({ ...data, viewer: data.viewer || prev?.viewer }));
    return data.retry;
  };
  const unpauseForProbe = (budget_usd) => act("unpause", { budget_usd, note: "unpaused next to the AI-limit probe status" });

  return (
    <div data-testid="spend-gate-page" className="min-h-screen bg-dots">
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold">Spend gate</h1>
          <Link to={adminBase()} data-testid="spend-gate-back" className="text-sm text-accent font-semibold hover:underline">
            {adminHomeLabel()}
          </Link>
        </div>

        {alert && (
          <div data-testid="spend-gate-alert" className="mt-6 rounded-2xl border border-rose-300 bg-rose-50 text-rose-900 px-4 py-3 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-bold text-sm">{alert.kind === "ai_limit" ? "AI service daily limit reached" : "Paid AI work is paused"}</p>
              <p className="text-sm mt-1">{alert.reason}</p>
              <p className="text-xs mt-1 opacity-80">
                {alert.kind === "ai_limit"
                  ? `Raised at ${alert.at} · ducks that need Gemini are parked and resume automatically once the AI service answers again.`
                  : `Paused at ${alert.at} · customer ducks are parked and resume automatically once the gate opens.`}
              </p>
            </div>
            <Button data-testid="spend-gate-ack" variant="outline" size="sm" className="rounded-full" disabled={busy === "ack-alert"} onClick={() => act("ack-alert")}>
              Dismiss
            </Button>
          </div>
        )}

        <AiLimitBanner aiLimit={data?.ai_limit} />
        <AiLimitProbe
          aiLimit={data?.ai_limit}
          gate={gate}
          viewer={data?.viewer}
          onRetry={retryProbe}
          onUnpause={unpauseForProbe}
          onRaise={setToday}
        />
        <AlertEmailStatus email={data?.alert_email} />

        {gate && !gate.blocks && (
          <div data-testid="spend-gate-monitor-banner" className="mt-6 rounded-2xl border border-sky-300 bg-sky-50 text-sky-900 px-4 py-3 text-sm">
            <p className="font-bold">Spend gate: {gate.mode_label}</p>
            <p className="mt-1">
              Customers are never blocked in this mode. Every paid call is recorded, and you are emailed at 50%, 80%
              and 100% of the {money(gate.soft_daily_usd)} soft daily figure, or when one duck passes{" "}
              {money(gate.per_duck_max_usd)}. The Pause button below always blocks, in every mode.
            </p>
            {gate.paused_by && <p className="mt-1 text-xs opacity-80">Paused by {gate.paused_by}{gate.paused_at ? ` on ${gate.paused_at}` : ""}</p>}
          </div>
        )}

        {gate && gate.configured === false && (
          <div data-testid="spend-gate-unconfigured" className="mt-4 rounded-2xl border border-amber-300 bg-amber-50 text-amber-900 px-4 py-3 text-sm">
            <p className="font-bold">No explicit spend-gate settings — running on defaults ({gate.mode})</p>
            <p className="mt-1">Set SPEND_GATE_MODE, SPEND_SOFT_DAILY_USD and OWNER_ALERT_EMAIL to make this deliberate.</p>
          </div>
        )}

        {loading && !data ? (
          <p data-testid="spend-gate-loading" className="mt-8 text-muted-foreground flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </p>
        ) : (
          <>
            <div className="mt-6 flex items-center gap-3">
              <span
                data-testid="spend-gate-state"
                className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-mono2 uppercase tracking-widest ${
                  paused ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-800"
                }`}
              >
                {paused ? <PauseCircle className="h-3.5 w-3.5" /> : <ShieldCheck className="h-3.5 w-3.5" />}
                {paused ? "Paused" : "Open"}
              </span>
              <span
                data-testid="spend-gate-mode"
                className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-mono2 uppercase tracking-widest ${
                  gate?.blocks ? "bg-amber-100 text-amber-800" : "bg-sky-100 text-sky-800"
                }`}
              >
                Mode: {gate?.mode}
              </span>
              <span className="text-xs text-muted-foreground">
                {gate?.environment} · UTC day {gate?.day} · month {money(gate?.month_spent_usd)}
              </span>
            </div>
            <p data-testid="spend-gate-reason" className="text-sm text-muted-foreground mt-3">{gate?.reason}</p>

            <div className="mt-6 grid gap-3 sm:grid-cols-3">
              <Stat testid="spend-gate-remaining" label="Budget left today" value={money(gate?.remaining_usd)} tone={paused ? "text-rose-700" : "text-emerald-700"} />
              <Stat testid="spend-gate-spent" label="Spent today" value={money(gate?.spent_usd)} />
              <Stat testid="spend-gate-budget" label="Budget today" value={money(gate?.budget_usd)} />
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Daily budget from env: {money(gate?.daily_budget_usd)} · single-unpause cap: {money(gate?.cap_usd)} · per-duck submission cap:{" "}
              {gate?.max_submissions_per_duck} · starts paused on a new day: {String(gate?.default_paused)} · owner alert email configured:{" "}
              {String(data?.env_hints?.owner_alert_email_set)}
            </p>

            <TodayBudgetCard gate={gate} viewer={data?.viewer} changes={data?.budget_changes} onSet={setToday} />

            <div className="mt-8 rounded-2xl border border-border bg-card p-4">
              <p className="font-heading font-bold">Controls</p>
              <div className="mt-3 flex flex-wrap items-end gap-3">
                <div>
                  <label htmlFor="gate-amount" className="block text-xs uppercase tracking-widest text-muted-foreground font-mono2">
                    Budget for today (USD)
                  </label>
                  <input
                    id="gate-amount"
                    data-testid="spend-gate-amount-input"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    inputMode="decimal"
                    placeholder={String(gate?.cap_usd ?? "")}
                    className="mt-1 w-40 rounded-full border border-border bg-background px-4 h-10 text-sm outline-none focus:ring-2 ring-accent/40"
                  />
                </div>
                <div className="flex-1 min-w-[200px]">
                  <label htmlFor="gate-note" className="block text-xs uppercase tracking-widest text-muted-foreground font-mono2">
                    Note / reason
                  </label>
                  <input
                    id="gate-note"
                    data-testid="spend-gate-note-input"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="why you are opening or closing the gate"
                    className="mt-1 w-full rounded-full border border-border bg-background px-4 h-10 text-sm outline-none focus:ring-2 ring-accent/40"
                  />
                </div>
                <Button
                  data-testid="spend-gate-unpause-btn"
                  className="rounded-full h-10 px-5 font-bold gap-2"
                  disabled={busy === "unpause" || !Number(amount)}
                  onClick={() => act("unpause", { budget_usd: Number(amount), note })}
                >
                  <PlayCircle className="h-4 w-4" /> Unpause with this budget
                </Button>
                <Button
                  data-testid="spend-gate-pause-btn"
                  variant="outline"
                  className="rounded-full h-10 px-5 font-bold gap-2"
                  disabled={busy === "pause"}
                  onClick={() => act("pause", { reason: note || "paused by the owner from Shopify admin" })}
                >
                  <PauseCircle className="h-4 w-4" /> Pause
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-3">
                Unpause amounts above the {money(gate?.cap_usd)} cap are clamped; only "Today's daily budget" above can go higher
                (max {money(gate?.override_max_usd ?? 50)}, owner only). Today's spend is kept when you re-open, so the daily ceiling always holds.
              </p>
            </div>

            <section className="mt-8 rounded-2xl border border-border bg-card p-4">
              <p className="font-heading font-bold">AI models in use</p>
              <p className="text-xs text-muted-foreground mt-1">
                Set these in the Secrets panel. An unrecognised id is ignored: the default is used and a warning is logged.
              </p>
              <ul data-testid="ai-models-list" className="mt-3 space-y-2">
                {(data?.ai_models || []).map((m) => (
                  <li key={m.name} className="rounded-xl border border-border bg-background px-4 py-2 text-sm">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <span className="font-mono2 text-xs uppercase tracking-widest text-muted-foreground">{m.name}</span>
                      <span className="font-mono2 font-semibold">{m.value}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {m.purpose} · {m.overridden ? "set in Secrets" : "default"}
                    </p>
                    {m.invalid && (
                      <p data-testid={`ai-model-invalid-${m.name}`} className="text-xs text-rose-700 mt-1 font-semibold">
                        The value in Secrets is not a recognised {m.kind} model id — using the default {m.default}.
                      </p>
                    )}
                    {m.retired_on && (
                      <p data-testid={`ai-model-retired-${m.name}`} className="text-xs text-amber-800 mt-1 font-semibold">
                        Google lists this id as retired on {m.retired_on}. It still works through the Universal Key proxy
                        today — switch as soon as a replacement id is accepted.
                      </p>
                    )}
                  </li>
                ))}
                {!(data?.ai_models || []).length && <li className="text-sm text-muted-foreground">Not reported.</li>}
              </ul>
            </section>

            <section className="mt-8 rounded-2xl border border-border bg-card p-4">
              <div className="flex items-baseline justify-between gap-3 flex-wrap">
                <p className="font-heading font-bold">Hourly rate limits</p>
                <span
                  data-testid="rate-limits-state"
                  className={`font-mono2 text-xs font-bold uppercase tracking-widest ${
                    data?.rate_limits?.creation_limits_enabled ? "text-emerald-700" : "text-amber-800"
                  }`}
                >
                  {data?.rate_limits?.creation_limits_enabled ? "creation limits on" : "creation limits off"}
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Per maker / per IP, each hour. Tune a creation limit in the Secrets panel with its{" "}
                <span className="font-mono2">RATE_LIMIT_…="per_maker,per_ip"</span> (0 = unlimited). Security limits are
                locked on: they ignore <span className="font-mono2">RATE_LIMITS_ENABLED</span> and no account is exempt.
                {" "}Exempt from creation limits: {data?.rate_limits?.exempt_email_count || 0} address
                {(data?.rate_limits?.exempt_email_count || 0) === 1 ? "" : "es"}.
              </p>
              <ul data-testid="rate-limits-list" className="mt-3 space-y-2">
                {(data?.rate_limits?.limits || []).map((r) => (
                  <li
                    key={r.action}
                    data-testid={`rate-limit-${r.action}`}
                    className="rounded-xl border border-border bg-background px-4 py-2 text-sm"
                  >
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <span className="font-mono2 text-xs uppercase tracking-widest text-muted-foreground">
                        {r.action}
                        {r.security && <span className="ml-2 text-rose-700 font-bold">locked</span>}
                      </span>
                      <span className="font-mono2 font-semibold">
                        {!r.enforced ? "off" : r.unlimited ? "unlimited" : `${r.per_user || "∞"} / ${r.per_ip || "∞"}`}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {r.security
                        ? "security limit · always enforced"
                        : `${r.overridden ? "set in Secrets" : "default"} · default ${r.default_per_user}/${r.default_per_ip} · ${r.env_var}`}
                    </p>
                    {r.invalid && (
                      <p data-testid={`rate-limit-invalid-${r.action}`} className="text-xs text-rose-700 mt-1 font-semibold">
                        The value in Secrets is not "per_maker,per_ip" (whole numbers, 0 = unlimited) — using the default{" "}
                        {r.default_per_user}/{r.default_per_ip}.
                      </p>
                    )}
                  </li>
                ))}
                {!(data?.rate_limits?.limits || []).length && (
                  <li className="text-sm text-muted-foreground">Not reported.</li>
                )}
              </ul>
            </section>

            <UserSpendTable />

            <section className="mt-8">
              <p className="font-heading font-bold">Per-duck provider submissions today</p>
              {(data?.submissions || []).length ? (
                <ul data-testid="spend-gate-submissions" className="mt-3 space-y-2">
                  {data.submissions.map((s) => (
                    <li key={s.id} className="flex items-center justify-between gap-3 rounded-xl border border-border bg-card px-4 py-2 text-sm">
                      <span className="truncate">{s.name || s.id}</span>
                      <span className="font-mono2 font-semibold">
                        {s.count}/{gate?.max_submissions_per_duck}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p data-testid="spend-gate-submissions-empty" className="text-sm text-muted-foreground mt-2">No paid submissions counted today.</p>
              )}
            </section>

            <section className="mt-8">
              <p className="font-heading font-bold">Ducks waiting for the gate, the AI service or a maker's allowance</p>
              {(data?.parked_ducks || []).length ? (
                <ul data-testid="spend-gate-parked" className="mt-3 space-y-2">
                  {data.parked_ducks.map((d) => (
                    <li key={d.id} data-testid={`spend-gate-parked-${d.id}`} className="flex items-center justify-between gap-3 rounded-xl border border-border bg-card px-4 py-2 text-sm">
                      <span className="truncate">{d.name || d.id}{d.cap_blocked && d.email ? <span className="text-muted-foreground"> · {d.email}</span> : null}</span>
                      <span className="text-muted-foreground font-mono2 text-xs">
                        {d.ai_limit_blocked
                          ? `AI service limit · ${d.ai_limit_stage} · ${d.ai_limit_blocked_at}`
                          : d.cap_blocked ? `maker's cap · ${d.cap_stage} · ${d.cap_blocked_at}` : `gate · ${d.gate_stage} · ${d.gate_blocked_at}`}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p data-testid="spend-gate-parked-empty" className="text-sm text-muted-foreground mt-2">No ducks are parked right now.</p>
              )}
            </section>

            <section className="mt-8">
              <p className="font-heading font-bold">Audit trail</p>
              <ul data-testid="spend-gate-audit" className="mt-3 space-y-2">
                {(data?.audit || []).map((a, i) => (
                  <li key={`${a.at}-${i}`} className="rounded-xl border border-border bg-card px-4 py-2 text-xs">
                    <span className="font-mono2 font-semibold">{a.action.replace("spend_gate.", "")}</span> · {a.at} · {a.staff_id || "system"}
                    {a.meta?.budget_usd !== undefined ? ` · ${money(a.meta.budget_usd)}` : ""}
                    {a.meta?.to_usd !== undefined ? ` · ${money(a.meta.from_usd)} → ${money(a.meta.to_usd)}${a.meta.above_cap ? " (above cap)" : ""}` : ""}
                    {a.meta?.reason ? ` · ${a.meta.reason}` : ""}
                    {a.meta?.note ? ` · ${a.meta.note}` : ""}
                  </li>
                ))}
                {!(data?.audit || []).length && <li className="text-sm text-muted-foreground">Nothing yet.</li>}
              </ul>
            </section>
          </>
        )}

        {error && (
          <p data-testid="spend-gate-error" className="mt-6 text-sm text-rose-700">
            {error}
          </p>
        )}
        <BuildVersion detailed />
      </main>
    </div>
  );
}

export default function ShopAdminSpendGate() {
  return (
    <ShopAdminGate>
      <SpendGateInner />
    </ShopAdminGate>
  );
}
